import router from "@/router";
import Swal from "sweetalert2/dist/sweetalert2.js";
import { clearForm } from "./formHelpers";
import { getWord } from "./translator";
import { RouteLocationRaw } from "vue-router";

class NotificationDialog {
    public static Notify(message: string, optionalMessage = "", optionalSmallText = "") {
        NotificationDialog.Toast('success', message, optionalMessage, optionalSmallText)
    }
    public static NotifyInfo(message: string, optionalMessage = "", optionalSmallText = "") {
        NotificationDialog.Toast('info', message, optionalMessage, optionalSmallText)
    }
    public static NotifyError(message: string, optionalMessage = "", optionalSmallText = "") {
        NotificationDialog.Toast('error', message, optionalMessage, optionalSmallText)
    }
    public static NotifyWarning(message: string, optionalMessage = "", optionalSmallText = "") {
        NotificationDialog.Toast('warning', message, optionalMessage, optionalSmallText)
    }

    public static Error(message = "", buttontext = "") {
        if (!buttontext) {
            buttontext = getWord("Ok")
        }
        if (!message) {
            message = getWord("SomethingWentWrong")
        }

        Swal.fire({
            html: `<div>${message}</div>`,
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: buttontext,
            customClass: {
                confirmButton: "btn btn-primary",
            },
        });
    }
    public static Info(message: string, buttontext = "") {
        buttontext = this.GetButtonText(buttontext);

        Swal.fire({
            text: message,
            icon: "info",
            buttonsStyling: false,
            confirmButtonText: buttontext,
            customClass: {
                confirmButton: "btn btn-primary",
            },
        });
    }
    public static Success(message: string, buttontext = "") {
        buttontext = this.GetButtonText(buttontext);

        Swal.fire({
            text: message,
            icon: "success",
            buttonsStyling: false,
            confirmButtonText: buttontext,
            customClass: {
                confirmButton: "btn btn-primary",
            },
        });
    }
    public static SuccessWithCallback(message: string, buttontext = "", callback: any) {
        buttontext = this.GetButtonText(buttontext);

        Swal.fire({
            text: message,
            icon: "success",
            buttonsStyling: false,
            confirmButtonText: buttontext,
            customClass: {
                confirmButton: "btn btn-primary",
            },
        }).then(() => {
            callback()
        })
    }
    public static Prompt(message: string, callback: any) {
        const confirmButtontext = this.GetButtonText(getWord("Ok"));
        const cancelButtontext = this.GetButtonText(getWord("Cancel"));

        Swal.fire({
            text: message,
            icon: "warning",
            buttonsStyling: false,
            confirmButtonText: confirmButtontext,
            cancelButtonText: cancelButtontext,
            showCancelButton: true,
            showConfirmButton: true,
            customClass: {
                confirmButton: "btn btn-primary",
                cancelButton: "btn btn-danger"
            }
        }).then((result) => {
            callback(result.isConfirmed)
        })
    }
    public static SuccessPromptRouter(firstRoute: RouteLocationRaw, firstRouteStr: string, secondRoute: RouteLocationRaw, secondRouteStr: string, title = "CreatedSuccessfully"): Promise<any> {
        return Swal.fire({
            title: getWord(title),
            showDenyButton: true,
            showCancelButton: true,
            showConfirmButton: true,
            confirmButtonText: getWord(firstRouteStr),
            denyButtonText: getWord(secondRouteStr),
            cancelButtonText: getWord("OK"),
        }).then((result) => {
            if (result.isConfirmed) {
                router.push(firstRoute)
            } else if (result.isDenied) {
                router.push(secondRoute)
            }
        })
    }
    public static SuccessPrompt(listRouteName: string, editRouteName: string, itemId: string, showViewPage = false, message?: string): Promise<any> {
        return Swal.fire({
            title: getWord("CreatedSuccessfully"),
            text: message || null,
            showDenyButton: true,
            showCancelButton: true,
            showConfirmButton: true,
            confirmButtonText: getWord("GoToList"),
            denyButtonText: showViewPage ? getWord("View") : getWord("Edit"),
            cancelButtonText: getWord("CreateNew"),
        }).then((result) => {
            if (result.isConfirmed) {
                router.push({ name: listRouteName })
            } else if (result.isDenied) {
                router.push({ name: editRouteName, params: { id: itemId } })
            }
            else {
                clearForm();
            }
        })
    }
    public static SuccessPromptWithoutEdit(listRouteName: string): Promise<any> {
        return Swal.fire({
            title: getWord("CreatedSuccessfully"),
            showDenyButton: true,
            showCancelButton: false,
            showConfirmButton: true,
            confirmButtonText: getWord("GoToList"),
            denyButtonText: getWord("CreateNew"),
        }).then((result) => {
            if (result.isConfirmed) {
                router.push({ name: listRouteName })
            }
            else if (result.isDenied) {
                clearForm();
            }
            else {
                router.push({ name: listRouteName })
            }
        })
    }
    public static HandleCreate() {
        NotificationDialog.Success(getWord("AddedSuccessfully"));
    }
    public static HandleUpdate() {
        NotificationDialog.Success(getWord("UpdatedSuccessfully"));
    }
    public static HandleDelete(callback: any) {
        NotificationDialog.SuccessWithCallback(getWord("DeletedSuccessfully"), getWord("Ok"), callback)
    }

    private static Toast(icon: 'error' | 'success' | 'info' | 'warning', message: string, optionalMessage = "", optionalSmallText = "") {
        const Toast = Swal.mixin({
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            didOpen: (toast) => {
                toast.addEventListener('mouseenter', Swal.stopTimer)
                toast.addEventListener('mouseleave', Swal.resumeTimer)
            }
        })

        if (message)
            message = getWord(message)
        else
            message = getWord("Successful")

        if (optionalMessage) {
            message = `${message}: (${optionalMessage})`
        }

        Toast.fire({
            icon: icon,
            title: message,
            text: optionalSmallText
        })
    }
    private static GetButtonText(buttonText = "") {
        if (!buttonText) {
            buttonText = getWord("Ok")
        }

        return buttonText;
    }
}

export default NotificationDialog;